import { useState, useEffect } from 'react';
import useDebounce from '@/hooks/useDebounce';
import Input from '@/components/Input';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useOrdersContext } from '@/contexts/OrderContext';
import Button from '@/components/Button';
import { useNavigate } from 'react-router-dom';

const DeliveryAddressInput = ({
	withoutValidationButton = false,
	setPropInput = () => { },
	getAddress = false,
	classes = '',
	divClass = '',
	suggestionsClass = 'w-full',
	withInputDiv = true,
	field = null,
	isDisabled = false,
	extraInputAttributes = {},
}) => {
	const [input, setInput] = useState('');
	const [suggestions, setSuggestions] = useState([]);
	const debouncedInput = useDebounce(input, 500); // 500 ms delay
	const { cart, setCart, getDefaultRestaurantId } = useOrdersContext();
	const [isSuggestionClicked, setIsSuggestionClicked] = useState(false);
	const [isSelectingAddress, setIsSelectingAddress] = useState(false);
	const navigate = useNavigate();

  const isCorsicaAddress = (address) => {
    if (!address) return false;
    const postalCode = String(address.postal_code || '').trim();
    const region = String(address.region || '').toLowerCase();
    const country = String(address.country || '').toLowerCase();
    const isFrance = !country || country === 'france' || country === 'fr';
    if (!isFrance) return false;
    return postalCode.startsWith('20') || region.includes('corse') || region.includes('corsica');
  };

	useEffect(() => {
		setInput(cart?.delivery_address?.address);
	}, []);

	useEffect(() => {
		if (debouncedInput?.length > 2 && !isSuggestionClicked && debouncedInput !== cart?.delivery_address?.address) {
			const fetchSuggestions = async (inputValue) => {
				try {
					const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/autocomplete`, {
						params: {
							input: inputValue,
						},
					});

					if (response.data.addresses && Array.isArray(response.data.addresses)) {
						setSuggestions(response.data.addresses);
					} else {
						setSuggestions([]);
						console.error("Format de réponse inattendu", response.data);
					}
				} catch (error) {
					console.error("Erreur lors de la récupération des suggestions d'adresses:", error);
					toast.error(error.message, {
						position: 'top-center',
						autoClose: 3500,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: false,
						draggable: true,
						theme: 'colored',
					});
				}
			};

			fetchSuggestions(debouncedInput);
		} else {
			setSuggestions([]);
		}
	}, [debouncedInput, isSuggestionClicked]);

	const handleChange = (event) => {
		setInput(event.target.value);
		setIsSuggestionClicked(false);

		if (withoutValidationButton) {
			setPropInput(event.target.value);
		}
	};

	const handleSuggestionClick = async (suggestion) => {
		setInput(suggestion.address);
		setSuggestions([]);
		setIsSuggestionClicked(true);
		setIsSelectingAddress(true);

		// Mettre d'abord à jour l'adresse
		setCart((prevSteps) => ({
			...prevSteps,
			delivery_address: suggestion,
		}));

		// Puis récupérer le restaurant avec priorité 1 et mettre à jour is_no_limit
		console.log('DeliveryAddressInput - Avant getDefaultRestaurantId, cart.is_no_limit:', cart.is_no_limit);
		await getDefaultRestaurantId();
		console.log('DeliveryAddressInput - Après getDefaultRestaurantId, cart.is_no_limit:', cart.is_no_limit);

		if (getAddress) {
			setPropInput(suggestion);
		}
		
		setTimeout(() => {
			setIsSelectingAddress(false);
		}, 100);
	};

	const handleNextStepClick = () => {
		console.log('DeliveryAddressInput - handleNextStepClick, cart.is_no_limit:', cart.is_no_limit);
		console.log('DeliveryAddressInput - handleNextStepClick, cart.is_no_limit_type:', cart.is_no_limit_type);
		
		if (cart.delivery_address) {
			if (!cart.is_no_limit) {
				console.log('DeliveryAddressInput - Navigation vers /commande/menu');
				navigate('/commande/menu', { replace: true });
			}

			if (cart.is_no_limit && cart.is_no_limit_type === 'list') {
				console.log('DeliveryAddressInput - Navigation vers /commande/liste-des-produits');
				navigate('/commande/liste-des-produits', { replace: true });
			}

			if (cart.is_no_limit && cart.is_no_limit_type === 'category' && cart.is_no_limit_selected_category) {
				console.log('DeliveryAddressInput - Navigation vers catégorie spécifique');
				navigate(`/commande/${cart.is_no_limit_selected_category.name.toLowerCase().split(' ')
					.join('-')
					.replace(',', '-')}`, { replace: true });
			}
		}
	};

	return (
		<>
			<Input
				handleChange={handleChange}
				value={input}
				labelText={field?.labelText}
				labelFor={field?.labelFor}
				id={field?.id}
				name={field?.name}
				type={field?.type}
				isRequired={field?.isRequired}
				placeholder={field?.placeholder}
				withSpaces={false}
				customClass={classes}
				divClass={divClass}
				withDiv={withInputDiv}
				extraAttributes={extraInputAttributes}
			/>
			{suggestions?.length > 0 && (
				<div
					className={`relative ${suggestionsClass ? 'col-span-2' : ''} ${withoutValidationButton ? 'w-full' : 'w-full mx-auto lg:w-3/5'
						}`}
				>
					<div className={`suggestions-container ${suggestionsClass}`}>
						{suggestions.map((suggestion, index) => (
							<div key={index} className="suggestion" onClick={() => handleSuggestionClick(suggestion)}>
								{suggestion.address}
							</div>
						))}
					</div>
				</div>
			)}

      {isCorsicaAddress(cart?.delivery_address) && (
        <div className="w-full mx-auto lg:w-3/5">
          <p className="text-center text-primary mt-2">Nous ne livrons pas encore en Corse</p>
        </div>
      )}

			{!withoutValidationButton && (
				<div className="w-3/5 md:w-1/5">
					<Button
            disabled={(cart.delivery_fee_applied?.id && !(isDisabled || isSelectingAddress)) || isCorsicaAddress(cart?.delivery_address)}
						action={null}
						handleClick={handleNextStepClick}
						text="Valider"
					/>
				</div>
			)}
		</>
	);
};

export default DeliveryAddressInput;
